import React, { useEffect, useContext } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import SignIn from "./templates/auth/SignIn";
import AdminPanel from "./templates/dashboard/AdminPanel";

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import { AuthProvider, AuthContext } from "@context/AuthProvider";
import { AccountProvider } from "@context/AccountProvider";
import { AlertProvider } from "@context/AlertProvider";

import { setupInterceptors } from "@api/axiosClients";
import { useLogoutMutation, useRefreshTokenMutation } from '@mutation/authMutation';

import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

import "typeface-inter";
import ResetPassword from "@templates/auth/ResetPassword";
import VerifyResetCode from "@templates/auth/VerifyResetCode";
import SetNewPassword from "@templates/auth/SetNewPassword";
import ConfirmEmail from "@templates/auth/ConfirmEmail";
import LoadingPage from "@templates/shared-theme/pages/LoadingPage";
import ChallengeLeaderboardPage from "@templates/dashboard/pages/ChallengeLeaderboardPage"

const queryClient = new QueryClient()




function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AlertProvider>
        <AuthProvider>
          <AxiosSetup>
            <AccountProvider>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Router>
                  <Routes>
                    <Route path="/" exact element={<RootRedirect />} />
                    <Route path="/admin/*" element={<ProtectedRoute component={AdminPanel} />} />
                    <Route path="/signin" element={<SignInRoute />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/verify-reset" element={<VerifyResetCode />} />
                    <Route path="/set-new-password" element={<SetNewPassword />} />
                    <Route path="/confirm-email" element={<ConfirmEmail />} />
                    <Route path="/leaderboard/:challengeUuid" element={<ChallengeLeaderboardPage/>} ></Route>
                  </Routes>
                </Router>
              </LocalizationProvider>
            </AccountProvider>
          </AxiosSetup>
        </AuthProvider>
      </AlertProvider>
    </QueryClientProvider>
  );
}

function AxiosSetup({ children }) {
  const logoutMutation = useLogoutMutation();
  const refreshTokenMutation = useRefreshTokenMutation();

  useEffect(() => {
    setupInterceptors(logoutMutation.mutate, refreshTokenMutation.mutate);
  }, [logoutMutation, refreshTokenMutation]);

  return <>{children}</>;
}

function RootRedirect() {
  const { isLoggedIn, isInit } = useContext(AuthContext);

  if (!isInit) return <LoadingPage />;

  if (isLoggedIn === false) return <Navigate to="/signin" replace />;

  return <Navigate to="/admin/dashboard" replace />
}

function ProtectedRoute({ component: Component }) {
  const { isLoggedIn, isInit } = useContext(AuthContext);

  if (!isInit) return <LoadingPage />;

  if (isLoggedIn === false) return <Navigate to="/signin" replace />;

  return <Component />;
}

function SignInRoute() {
  const { isLoggedIn, isInit } = useContext(AuthContext);

  if (!isInit) return <LoadingPage />;

  if (isLoggedIn === false) return <SignIn />;

  return <Navigate to="/" replace />;
}

export default App;